import { useEffect, useMemo, useState } from 'react'
import { Card, Button, Text, Icon } from '@nimbus-ds/components'
import { ArrowRightIcon } from '@nimbus-ds/icons'
import { formatCurrency } from '@/lib/utils'
import { funnelService } from '@/api/funnel'
import { goalService, type Goal, type Actionable } from '@/api/goals'
import { authService } from '@/api/auth'

// ───────────────────────────────────────────────────────────────────────────
// MerchantFunnel — the funnel-centric MERCHANT screen (prototype).
//
// Thesis under test: a merchant should see ONE picture they already understand
// (their own store's buying steps), ONE leak called out, and ONE verb — nothing
// else. All the statistical machinery (BB+ROPE, mix-guard, SRM, segments) runs
// UNDERNEATH to decide WHICH leak is worth surfacing; none of that vocabulary
// reaches this screen. Consultant depth is a separate tier, not a toggle here.
//
// Data: GET /api/store/funnel/analytics → `tnx_analytics`. No per-step revenue
// exists, so we rank leaks by RECOVERABLE PURCHASES (sessions lost × the
// downstream probability they'd have converted), then multiply by a single
// store-level AOV only to express the ceiling in money. AOV is a constant across
// steps, so it never changes WHICH leak wins — only how it's phrased.
// ───────────────────────────────────────────────────────────────────────────

// Shape of each `step_analysis[stepId]` entry. No interface exists in the codebase
// (the store funnel response is `any`); these are the fields the pipeline emits.
interface StepAnalysis {
  step_name: string
  step_order: number
  step_type?: string
  is_conversion?: boolean
  total_sessions: number
  drop_off_rate?: number
  sessions_dropped?: number
  progression_rate?: number
  conversion_rate_reached?: number
}

type StepRow = StepAnalysis & { id: string }

interface StoreFunnelAnalytics {
  step_analysis?: Record<string, StepAnalysis>
  total_conversions?: number
  product_analytics?: { product_performance?: { total_product_revenue?: number } }
  off_storefront_orders?: { count: number; revenue: number } | null
}

interface Leak {
  fromId: string
  fromName: string
  toName: string
  fromSessions: number
  dropped: number
  dropPct: number
  recoveredPurchases: number
  recoveredMoney: number // ceiling: if the entire drop were recovered
}

// Find the actionable a consultant has bound to this funnel step (consultant-in-command:
// the merchant never invents the verb — it only appears if a consultant authored one).
function actionForStep(goals: Goal[], stepId: string): Actionable | null {
  for (const g of goals) {
    for (const a of g.actionables || []) {
      if (a.status !== 'active' && a.status !== 'proposed') continue
      const bound = a.indicators?.some(
        (ind) => ind.binding?.kind === 'funnel_step' && ind.binding?.step_id === stepId
      )
      if (bound) return a
    }
  }
  return null
}

function derive(a: StoreFunnelAnalytics): {
  steps: StepRow[]
  firstSessions: number
  finalConversions: number
  leak: Leak | null
  coveragePct: number | null
} | null {
  const steps = Object.entries(a.step_analysis || {})
    .map(([id, s]) => ({ id, ...s }))
    .sort((x, y) => (x.step_order ?? 0) - (y.step_order ?? 0))
  if (steps.length < 2) return null

  const firstSessions = steps[0].total_sessions || 0
  if (firstSessions <= 0) return null

  const convStep = steps.find((s) => s.is_conversion) || steps[steps.length - 1]
  const finalConversions =
    typeof a.total_conversions === 'number' ? a.total_conversions : convStep.total_sessions || 0

  const webRevenue = a.product_analytics?.product_performance?.total_product_revenue || 0
  const aov = finalConversions > 0 ? webRevenue / finalConversions : 0

  // Rank each step→next transition by recoverable purchases.
  let leak: Leak | null = null
  for (let i = 0; i < steps.length - 1; i++) {
    const from = steps[i]
    const to = steps[i + 1]
    const fromSessions = from.total_sessions || 0
    const toSessions = to.total_sessions || 0
    const dropped = Math.max(0, fromSessions - toSessions)
    if (dropped <= 0 || fromSessions <= 0) continue
    // Probability a session that reached the NEXT step eventually buys. For the
    // last transition (next = conversion) this is 1 → drops there are pure money.
    const downstreamConv = toSessions > 0 ? finalConversions / toSessions : 0
    const recoveredPurchases = dropped * downstreamConv
    const candidate: Leak = {
      fromId: from.id,
      fromName: from.step_name,
      toName: to.step_name,
      fromSessions,
      dropped,
      dropPct: Math.round((dropped / fromSessions) * 100),
      recoveredPurchases,
      recoveredMoney: recoveredPurchases * aov,
    }
    if (!leak || candidate.recoveredPurchases > leak.recoveredPurchases) leak = candidate
  }

  const offRevenue = a.off_storefront_orders?.revenue || 0
  const coveragePct =
    offRevenue > 0 && webRevenue + offRevenue > 0
      ? Math.round((webRevenue / (webRevenue + offRevenue)) * 100)
      : null

  return { steps, firstSessions, finalConversions, leak, coveragePct }
}

export function MerchantFunnel() {
  const [analytics, setAnalytics] = useState<StoreFunnelAnalytics | null>(null)
  const [goals, setGoals] = useState<Goal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const storeId = authService.getCurrentStoreId()
    if (!storeId) {
      setError('No pudimos identificar tu tienda.')
      setLoading(false)
      return
    }
    Promise.all([
      funnelService.getStoreFunnelAnalytics(storeId, 7),
      goalService.listGoals(7).catch(() => ({ goals: [] as Goal[] })),
    ])
      .then(([funnel, g]: [any, { goals: Goal[] }]) => {
        setAnalytics(funnel?.tnx_analytics || null)
        setGoals(g?.goals || [])
      })
      .catch((e) => setError(e?.message || 'No pudimos cargar tu embudo.'))
      .finally(() => setLoading(false))
  }, [])

  const d = useMemo(() => (analytics ? derive(analytics) : null), [analytics])
  const action = useMemo(
    () => (d?.leak ? actionForStep(goals, d.leak.fromId) : null),
    [goals, d]
  )

  if (loading) return <Text textAlign="center">Cargando tu embudo…</Text>
  if (error)
    return (
      <Card>
        <Card.Body>
          <Text textAlign="center">{error}</Text>
        </Card.Body>
      </Card>
    )
  if (!d)
    return (
      <Card>
        <Card.Body>
          <Text textAlign="center">
            Todavía estamos juntando datos de tu embudo. En unos días vas a ver tus pasos acá.
          </Text>
        </Card.Body>
      </Card>
    )

  const { steps, firstSessions, finalConversions, leak, coveragePct } = d

  return (
    <div className="mx-auto max-w-md space-y-5">
      {/* Header: one number that matters */}
      <div className="space-y-1">
        <Text fontSize="caption">Tu embudo · últimos 7 días</Text>
        <Text className="tabular-nums" fontSize="highlight" fontWeight="bold" color="neutral-textHigh">
          {finalConversions} <Text as="span">ventas</Text>
        </Text>
      </div>

      {/* The funnel itself — the picture the merchant already understands */}
      <Card>
        <Card.Body>
          <div className="space-y-2">
            {steps.map((s) => {
              const pct = Math.max(2, Math.round((s.total_sessions / firstSessions) * 100))
              const isLeakFrom = leak?.fromId === s.id
              return (
                <div key={s.id} className="space-y-1">
                  <div className="flex items-baseline justify-between">
                    <Text color="neutral-textHigh" fontWeight={isLeakFrom ? 'bold' : 'regular'}>
                      {s.step_name}
                    </Text>
                    <Text className="tabular-nums">
                      {s.total_sessions.toLocaleString('es-AR')}
                    </Text>
                  </div>
                  <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full rounded-full ${isLeakFrom ? 'bg-amber-500' : 'bg-primary/70'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </Card.Body>
      </Card>

      {/* The single leak + the single verb */}
      {leak ? (
        <Card className="border-amber-300 bg-amber-50/60">
          <Card.Body>
            <div className="space-y-3">
              <Text className="leading-relaxed" color="neutral-textHigh">
                De cada 100 personas que llegan a <strong>«{leak.fromName}»</strong>,{' '}
                <strong>{leak.dropPct}</strong> se van sin pasar a <strong>«{leak.toName}»</strong>.
                Es donde más ventas se te escapan.
              </Text>
              {leak.recoveredMoney > 0 && (
                <Text>
                  Recuperar esta fuga vale hasta{' '}
                  <strong className="text-foreground">{formatCurrency(leak.recoveredMoney)}</strong> por semana.
                </Text>
              )}
              {action ? (
                <Button appearance="primary" fullWidth>
                  {action.name}
                  <Icon source={<ArrowRightIcon />} color="currentColor" />
                </Button>
              ) : (
                <Text className="rounded-md bg-background/60 px-3 py-2" fontSize="caption">
                  Tu consultor va a proponer una acción para esto.
                </Text>
              )}
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body>
            <Text textAlign="center">
              Tu embudo viene parejo — no hay una fuga clara esta semana. 👌
            </Text>
          </Card.Body>
        </Card>
      )}

      {/* Honesty line: when off-storefront (POS) is material, say so — don't let the
          funnel pretend to cover sales it never saw. */}
      {coveragePct !== null && coveragePct < 95 && (
        <Text textAlign="center" fontSize="caption">
          Este embudo cubre el {coveragePct}% de tus ventas online — el resto entró por tu local/POS y
          no aparece acá.
        </Text>
      )}
    </div>
  )
}
